/**
 * useDocumentDetail — TanStack Query hook for the inline PDF viewer's data
 * (dms/spec.md §M11, §4.5).
 *
 * live mode → GET /documents/:id (fetchDocumentDetail).
 * mock mode → DOCUMENT_DETAIL_MOCK when the id matches, else `null` (renders
 *             the viewer's "Dokument nicht gefunden" state — same honesty as
 *             `usePoliceDetail`'s not-found path). `null`, never `undefined`
 *             — TanStack Query treats an `undefined` queryFn result as "no
 *             data returned" and warns.
 */
import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchDocumentDetail } from '../api/documents-api';
import { DOCUMENT_DETAIL_MOCK } from '../mock';

export function useDocumentDetail(id: string) {
  const live = isLiveData();

  const query = useQuery({
    queryKey: qk.documentDetail(id),
    queryFn: () =>
      live
        ? fetchDocumentDetail(id)
        : Promise.resolve(DOCUMENT_DETAIL_MOCK.id === id ? DOCUMENT_DETAIL_MOCK : null),
    enabled: id.length > 0,
    ...(live ? {} : { staleTime: Infinity }),
  });

  return {
    detail: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
